/**
 * SMS History
 * Displays outbound SMS send attempts and latest statuses.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  type IColumn,
  MessageBarType,
  DefaultButton,
  Spinner,
  SpinnerSize
} from '@fluentui/react';
import { useOnCallManager } from '../contexts/OnCallManagerContext';
import type { OnCallSmsHistoryItem } from '../types';
import { formatUtcAsLocalDateTime } from '../utils/dateTime';

interface SmsHistoryProps {
  onNotification: (type: MessageBarType, message: string) => void;
}

const SmsHistory: React.FC<SmsHistoryProps> = ({ onNotification }) => {
  const { smsHistory, refreshSmsHistory, loading } = useOnCallManager();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Mount-only load. Avoid depending on refresh/onNotification identity (fetch loops).
  useEffect(() => {
    refreshSmsHistory().catch(err => {
      const message = err instanceof Error ? err.message : 'Failed to load SMS history';
      onNotification(MessageBarType.error, `Error: ${message}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshSmsHistory();
      onNotification(MessageBarType.success, 'SMS history refreshed');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh SMS history';
      onNotification(MessageBarType.error, `Error: ${message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: 'sentAt',
        name: 'Sent',
        minWidth: 150,
        maxWidth: 200,
        onRender: (item: OnCallSmsHistoryItem) => formatUtcAsLocalDateTime(item.sentAt)
      },
      {
        key: 'recipient',
        name: 'Recipient Member',
        minWidth: 180,
        maxWidth: 260,
        onRender: (item: OnCallSmsHistoryItem) => (
          <Text variant="small">{item.recipientUserId || 'Unknown'}</Text>
        )
      },
      {
        key: 'phone',
        name: 'Phone',
        minWidth: 120,
        maxWidth: 160,
        onRender: (item: OnCallSmsHistoryItem) => (
          <Text variant="small">{item.recipientPhoneNumber || 'N/A'}</Text>
        )
      },
      {
        key: 'status',
        name: 'Status',
        minWidth: 100,
        maxWidth: 140,
        onRender: (item: OnCallSmsHistoryItem) => {
          const status = item.latestStatus ?? (item.sendSucceeded ? 'sent' : 'failed');
          const isFailure = status.toLowerCase() === 'failed' || status.toLowerCase() === 'undelivered';
          return (
            <Text variant="small" styles={{ root: { color: isFailure ? '#d13438' : '#107c10' } }}>
              {status}
            </Text>
          );
        }
      },
      {
        key: 'association',
        name: 'Association',
        minWidth: 120,
        maxWidth: 180,
        onRender: (item: OnCallSmsHistoryItem) => {
          if (item.notificationId) {
            return <Text variant="small">Notification #{item.notificationId}</Text>;
          }
          return <Text variant="small">Unassociated</Text>;
        }
      },
      {
        key: 'ticket',
        name: 'Ticket',
        minWidth: 80,
        maxWidth: 100,
        onRender: (item: OnCallSmsHistoryItem) => (
          <Text variant="small">{item.ticketId ?? 'N/A'}</Text>
        )
      }
    ],
    []
  );

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
          SMS History
        </Text>
        <DefaultButton
          text="Refresh"
          iconProps={{ iconName: 'Refresh' }}
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
        />
      </Stack>

      <Text variant="medium" styles={{ root: { color: '#605e5c' } }}>
        Displays recent outbound SMS messages, recipients, and latest Twilio status.
      </Text>

      {loading || isRefreshing ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Spinner size={SpinnerSize.large} label="Loading SMS history..." />
        </Stack>
      ) : smsHistory.length === 0 ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Text variant="large" styles={{ root: { color: '#a19f9d' } }}>
            No SMS history found
          </Text>
        </Stack>
      ) : (
        <DetailsList
          items={smsHistory}
          columns={columns}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
          isHeaderVisible={true}
          styles={{
            root: {
              '.ms-DetailsRow': {
                borderBottom: '1px solid #edebe9'
              }
            }
          }}
        />
      )}
    </Stack>
  );
};

export default SmsHistory;
